/**
 * 30-day activity bar chart (brief §progress: "activity bar chart (recharts,
 * 30 d)"). Two series per day — practice/exam attempts and flashcard
 * reviews — read off `GET /api/progress/overview`'s `activity` array.
 */
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { useTheme } from "@client/app/theme";
import { useLang, useT } from "@client/i18n";
import { formatDate, formatNumber } from "@client/lib/format";
import type { ActivityDay } from "./types";

interface ChartRow {
  dayKey: string;
  attempts: number;
  reviews: number;
  correctCount: number;
}

function TooltipContent({
  active,
  payload,
  lang,
}: {
  active?: boolean;
  payload?: readonly { payload?: ChartRow }[];
  lang: "sv" | "en";
}) {
  const t = useT();
  const row = active ? payload?.[0]?.payload : undefined;
  if (!row) return null;
  return (
    <div className="surface px-3 py-2 text-xs shadow-card">
      <p className="mb-1 font-medium text-ink-800 dark:text-ink-100">
        {formatDate(new Date(`${row.dayKey}T12:00:00Z`).getTime(), lang)}
      </p>
      <p className="text-ink-600 dark:text-ink-400">
        {t("progress.activity.attempts")}: {formatNumber(row.attempts, lang)} (
        {formatNumber(row.correctCount, lang)} {t("progress.activity.correctSuffix")})
      </p>
      <p className="text-ink-600 dark:text-ink-400">
        {t("progress.activity.reviews")}: {formatNumber(row.reviews, lang)}
      </p>
    </div>
  );
}

export function ActivityChart({ days }: { days: ActivityDay[] }) {
  const t = useT();
  const { lang } = useLang();
  const { theme } = useTheme();
  const dark = theme === "dark";

  const total = days.reduce((sum, d) => sum + d.attempts + d.reviews, 0);
  if (total === 0) {
    return <p className="text-sm text-ink-600 dark:text-ink-400">{t("progress.activity.empty")}</p>;
  }

  const attemptsColor = dark ? "#818cf8" : "#4f46e5"; // accent-400 / accent-600
  const reviewsColor = dark ? "#475569" : "#cbd5e1"; // ink-600 / ink-300

  const data: ChartRow[] = days.map((d) => ({
    dayKey: d.dayKey,
    attempts: d.attempts,
    reviews: d.reviews,
    correctCount: d.correctCount,
  }));

  return (
    <div role="img" aria-label={t("progress.activity.title")} className="h-48 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
          <CartesianGrid
            strokeDasharray="3 3"
            stroke={dark ? "#1e293b" : "#e2e8f0"}
            vertical={false}
          />
          <XAxis
            dataKey="dayKey"
            tickFormatter={(value: string) => value.slice(8, 10)}
            tick={{ fontSize: 11, fill: dark ? "#94a3b8" : "#64748b" }}
            interval={4}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            allowDecimals={false}
            tick={{ fontSize: 11, fill: dark ? "#94a3b8" : "#64748b" }}
            width={28}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip
            content={(props) => <TooltipContent {...props} lang={lang} />}
            cursor={{ fill: dark ? "#1e293b" : "#f1f5f9" }}
          />
          {/*
           * `isAnimationActive={false}`: recharts' default grow-in animation
           * can race `ResponsiveContainer`'s own sizing on mount at a narrow
           * (mobile) viewport and leave the bars at their zero-height start —
           * see `teacher/ChapterAccuracyChart.tsx`. Same chart shape, same
           * exposure, so the same fix.
           */}
          <Bar
            dataKey="attempts"
            name={t("progress.activity.attempts")}
            fill={attemptsColor}
            radius={[2, 2, 0, 0]}
            isAnimationActive={false}
          />
          <Bar
            dataKey="reviews"
            name={t("progress.activity.reviews")}
            fill={reviewsColor}
            radius={[2, 2, 0, 0]}
            isAnimationActive={false}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export default ActivityChart;
